import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { FadeUp } from "../components/Reveal";
import { StatusBadge } from "../portal/PortalDashboard";

export default function AdminCalendar() {
  const [bookings, setBookings] = useState([]);
  const [cursor, setCursor] = useState(() => new Date());
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  }, []);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const byDate = useMemo(() => {
    const map = {};
    bookings.filter((b) => ["pending", "approved"].includes(b.status)).forEach((b) => {
      (map[b.date] = map[b.date] || []).push(b);
    });
    return map;
  }, [bookings]);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const dateStr = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const monthName = cursor.toLocaleString("default", { month: "long" });
  const selectedBookings = selected ? byDate[selected] || [] : [];

  return (
    <div data-testid="admin-calendar">
      <FadeUp>
        <h1 className="font-serif text-3xl sm:text-4xl text-obsidian">Studio <span className="italic text-purple-700">calendar</span></h1>
        <p className="text-sm text-slate-500 mt-2">Pending requests in amber, confirmed sessions in emerald.</p>
      </FadeUp>

      <FadeUp delay={0.1} className="mt-10 bg-white rounded-3xl border border-purple-100 p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <p className="font-serif text-2xl" data-testid="calendar-month-label">{monthName} <span className="text-slate-400">{year}</span></p>
          <div className="flex gap-2">
            <button onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="calendar-prev"
              className="w-9 h-9 rounded-full border border-purple-100 flex items-center justify-center text-slate-500 hover:border-gold hover:text-gold transition-colors" aria-label="Previous month">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="calendar-next"
              className="w-9 h-9 rounded-full border border-purple-100 flex items-center justify-center text-slate-500 hover:border-gold hover:text-gold transition-colors" aria-label="Next month">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => <span key={d} className="py-2">{d}</span>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <span key={`e-${i}`} />;
            const ds = dateStr(d);
            const items = byDate[ds] || [];
            const isSel = selected === ds;
            return (
              <button key={ds} onClick={() => setSelected(isSel ? null : ds)} data-testid={`calendar-day-${ds}`}
                className={`min-h-[64px] sm:min-h-[84px] rounded-xl border p-1.5 text-left transition-colors ${
                  isSel ? "border-gold bg-gold/10" : items.length ? "border-purple-200 bg-lilac-mist hover:border-gold" : "border-transparent hover:border-purple-100"
                }`}>
                <span className="text-xs font-mono text-slate-500">{d}</span>
                <div className="mt-1 space-y-1">
                  {items.slice(0, 2).map((b) => (
                    <span key={b.booking_id} className={`block text-[9px] font-mono px-1.5 py-0.5 rounded truncate ${
                      b.status === "approved" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                    }`}>
                      {b.time} {b.client_name.split(" ")[0]}
                    </span>
                  ))}
                  {items.length > 2 && <span className="block text-[9px] text-slate-400 px-1">+{items.length - 2} more</span>}
                </div>
              </button>
            );
          })}
        </div>
      </FadeUp>

      {selected && (
        <FadeUp className="mt-6 bg-white rounded-3xl border border-purple-100 p-7" data-testid="calendar-day-detail">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-slate-400">Sessions on {selected}</p>
          <div className="mt-4 space-y-3">
            {selectedBookings.map((b) => (
              <div key={b.booking_id} className="flex flex-wrap items-center gap-4 p-4 rounded-2xl bg-lilac-mist">
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm font-medium">{b.client_name} — {b.service_name}</p>
                  <p className="text-xs text-slate-500 mt-1">{b.time} · {b.location_name} · ${b.price}</p>
                </div>
                <StatusBadge status={b.status} />
              </div>
            ))}
            {selectedBookings.length === 0 && <p className="text-sm text-slate-400 italic">Nothing booked this day.</p>}
          </div>
        </FadeUp>
      )}
    </div>
  );
}
